import { detectLanguageWithScores, containsLatin } from "./detectLanguage";
import { callGemini, type GeminiContentPart } from "@/lib/gemini";
import type { Card, DeckLang } from "@/types/linguadeck";

const LINE_BREAK_REGEX = /\r\n?/g;
const NOISE_LINE_REGEX = /^[\s\-–—_=~•·▪◦●*#|:;,.()[\]{}\\/]+$/u;
const LIST_PREFIX_REGEX = /^\s*(?:[-*•●▪◦◆◇▶►▸▹▫□■☑✅✔✦✨📌📍]+|(?:\d+|[A-Za-z])[.)])\s*/u;
const EDGE_DECORATION_REGEX = /^[\s"'“”„`~*_•·▪◦●✦✨📚📝🔥⭐]+|[\s"'“”„`~*_•·▪◦●✦✨📚📝🔥⭐]+$/gu;
const IMAGE_EXTENSION_REGEX = /\.(png|jpe?g|webp)$/i;
const IMAGE_MIME_REGEX = /^image\/(png|jpe?g|webp)$/i;
const OCR_PROMPT =
  "Transcribe the visible study text from this image as plain text only. Preserve headers and line breaks. If the image contains bilingual vocabulary pairs, format them one pair per line as term - translation using the original wording. Do not translate, explain, or add markdown.";

type Translator = (key: string, opts?: Record<string, unknown>) => string;

export interface ParsedVocabularyPair {
  front: string;
  back: string;
  rawLine: string;
}

export interface ParsedFlashcardInput {
  sourceText: string;
  sourceSignature: string;
  title: string;
  pairs: ParsedVocabularyPair[];
  count: number;
  sourceLang: DeckLang | null;
  targetLang: DeckLang | null;
  isStructured: boolean;
}

/** A segment of text identified as a topic block (for multi-module splitting). */
export interface TopicBlock {
  /** Raw text belonging to this topic */
  rawText: string;
  /** Detected or inferred module title */
  title: string;
  /** Optional subtitle (e.g. translation in brackets) */
  subtitle?: string;
  /** Line index where this block starts (0-based) */
  startLine: number;
}

/** A fully parsed module with pairs and languages. */
export interface ParsedModule {
  title: string;
  subtitle?: string;
  pairs: ParsedVocabularyPair[];
  sourceLang: DeckLang | null;
  targetLang: DeckLang | null;
}

/** Multi-module parse result. */
export interface ParsedFlashcardInputMulti {
  sourceText: string;
  sourceSignature: string;
  modules: ParsedModule[];
  /** True if multiple distinct topics were detected */
  hasMultipleTopics: boolean;
  /** Total pair count across all modules */
  totalCount: number;
}

type LanguageScoreMap = Record<DeckLang, number>;

const LATIN_CODES: DeckLang[] = ["en", "es", "fr", "de", "it", "nl", "pl", "pt-BR", "tr"];
const EMPTY_SCORES: LanguageScoreMap = {
  en: 0,
  es: 0,
  fr: 0,
  de: 0,
  it: 0,
  nl: 0,
  pl: 0,
  "pt-BR": 0,
  tr: 0,
  ko: 0,
  ru: 0,
  uk: 0,
};

const LATIN_LANGUAGE_HINTS: Record<
  typeof LATIN_CODES[number],
  { keywords: string[]; fragments: string[]; chars: string[] }
> = {
  en: {
    keywords: [
      "the",
      "and",
      "with",
      "for",
      "travel",
      "weather",
      "school",
      "happy",
      "sad",
      "city",
      "country",
      "book",
      "ticket",
      "airport",
    ],
    fragments: ["th", "ing", "tion", "ness", "ship", "ly", "ow", "ea"],
    chars: [],
  },
  es: {
    keywords: ["el", "la", "los", "las", "un", "una", "gracias", "hola", "viaje", "tiempo", "ciudad", "escuela"],
    fragments: ["ción", "que", "ll", "ado", "ada", "mente", "está"],
    chars: ["á", "é", "í", "ó", "ú", "ñ", "¿", "¡"],
  },
  fr: {
    keywords: ["le", "la", "les", "un", "une", "des", "bonjour", "merci", "voyage", "météo", "ville", "école"],
    fragments: ["eau", "eux", "ait", "ette", "tion", "ique"],
    chars: ["à", "â", "ç", "é", "è", "ê", "ë", "î", "ï", "ô", "ù", "û", "ü", "ÿ", "œ"],
  },
  de: {
    keywords: ["der", "die", "das", "ein", "eine", "hallo", "danke", "reise", "wetter", "stadt", "schule", "flughafen"],
    fragments: ["sch", "ung", "keit", "heit", "chen", "lich", "tz", "ei", "ie"],
    chars: ["ä", "ö", "ü", "ß"],
  },
  it: {
    keywords: ["il", "lo", "la", "gli", "una", "uno", "ciao", "grazie", "viaggio", "tempo", "città", "scuola"],
    fragments: ["zione", "gli", "che", "mente", "tta", "zione"],
    chars: ["à", "è", "é", "ì", "ò", "ù"],
  },
  nl: {
    keywords: ["de", "het", "een", "hallo", "dank", "reis", "weer", "stad", "school"],
    fragments: ["ij", "sch", "lijk", "heid", "een"],
    chars: [],
  },
  pl: {
    keywords: ["jest", "nie", "dziękuję", "cześć", "podróż", "pogoda", "miasto", "szkoła"],
    fragments: ["sz", "cz", "rz", "owa", "anie", "ować"],
    chars: ["ą", "ć", "ę", "ł", "ń", "ó", "ś", "ź", "ż"],
  },
  "pt-BR": {
    keywords: ["o", "a", "os", "as", "um", "uma", "obrigado", "olá", "viagem", "tempo", "cidade", "escola"],
    fragments: ["ção", "ções", "nh", "lh", "dade", "mente"],
    chars: ["á", "â", "ã", "à", "ç", "é", "ê", "í", "ó", "ô", "õ", "ú"],
  },
  tr: {
    keywords: ["bir", "ve", "çok", "merhaba", "teşekkürler", "seyahat", "hava", "şehir", "okul"],
    fragments: ["iyor", "mak", "mek", "lar", "ler", "lik"],
    chars: ["ç", "ğ", "ı", "İ", "ö", "ş", "ü"],
  },
};

function normalizeInput(text: string): string {
  return text
    .replace(LINE_BREAK_REGEX, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .trim();
}

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function stripLinePrefix(line: string): string {
  return line.replace(LIST_PREFIX_REGEX, "").trim();
}

function trimDecorativeEdges(line: string): string {
  return line.replace(EDGE_DECORATION_REGEX, "").trim();
}

function cleanLine(line: string): string {
  return trimDecorativeEdges(stripLinePrefix(line));
}

function isSupportedImage(file: File): boolean {
  return IMAGE_MIME_REGEX.test(file.type) || IMAGE_EXTENSION_REGEX.test(file.name);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function splitRowSegments(line: string): string[] {
  const segments = line
    .split(/\s*(?:;|•|·|▪|◦|●)\s*/u)
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.length > 0 ? segments : [line];
}

function isValidPairSide(value: string): boolean {
  const cleaned = normalizeWhitespace(value);
  return !!cleaned && cleaned.length <= 160 && !NOISE_LINE_REGEX.test(cleaned) && /[\p{L}\p{N}]/u.test(cleaned);
}

function buildPair(front: string, back: string, rawLine: string): ParsedVocabularyPair | null {
  const cleanedFront = normalizeWhitespace(front.replace(/[|]+$/g, ""));
  const cleanedBack = normalizeWhitespace(back.replace(/^[|]+/g, ""));
  if (!isValidPairSide(cleanedFront) || !isValidPairSide(cleanedBack)) return null;
  return { front: cleanedFront, back: cleanedBack, rawLine };
}

function parsePairSegment(line: string): ParsedVocabularyPair | null {
  const candidate = cleanLine(line);
  if (!candidate || NOISE_LINE_REGEX.test(candidate)) return null;

  const patterns = [
    /^(.+?)\s*(?:→|->|=>)\s*(.+)$/u,
    /^(.+?)\s+(?:—|–)\s+(.+)$/u,
    /^(.+?)\s+-\s+(.+)$/u,
    /^(.+?)\s*:\s+(.+)$/u,
    /^(.+?)\s*=\s*(.+)$/u,
    /^(.+?)(?:—|–)(.+)$/u,
  ];

  for (const pattern of patterns) {
    const match = candidate.match(pattern);
    if (!match) continue;
    const pair = buildPair(match[1], match[2], candidate);
    if (pair) return pair;
  }

  return null;
}

function parseLinePairs(line: string): ParsedVocabularyPair[] {
  const cleaned = line.trim();
  if (!cleaned || NOISE_LINE_REGEX.test(cleaned)) return [];
  return splitRowSegments(cleaned)
    .map((segment) => parsePairSegment(segment))
    .filter((pair): pair is ParsedVocabularyPair => !!pair);
}

function sanitizeTitleCandidate(line: string): string {
  return normalizeWhitespace(trimDecorativeEdges(stripLinePrefix(line)).replace(/[.。:：–—-]+$/u, ""));
}

function isProbableTitleLine(line: string): boolean {
  const cleaned = sanitizeTitleCandidate(line);
  if (!cleaned) return false;
  if (parsePairSegment(cleaned)) return false;
  if (cleaned.length < 2 || cleaned.length > 80) return false;
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length > 10) return false;
  return /[\p{L}\p{N}]/u.test(cleaned);
}

export function extractStructuredPairs(text: string): ParsedVocabularyPair[] {
  const normalized = normalizeInput(text);
  if (!normalized) return [];

  const seen = new Set<string>();
  const pairs: ParsedVocabularyPair[] = [];

  for (const line of normalized.split("\n")) {
    for (const pair of parseLinePairs(line)) {
      const key = `${pair.front.toLowerCase()}__${pair.back.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push(pair);
    }
  }

  return pairs;
}

export function extractModuleTitle(text: string, pairs: ParsedVocabularyPair[] = extractStructuredPairs(text)): string {
  const normalized = normalizeInput(text);
  if (!normalized || pairs.length === 0) return "";

  const lines = normalized.split("\n");
  let firstPairIndex = lines.length;

  for (let i = 0; i < lines.length; i += 1) {
    if (parseLinePairs(lines[i]).length > 0) {
      firstPairIndex = i;
      break;
    }
  }

  const introCandidates = lines
    .slice(0, firstPairIndex)
    .map((line) => sanitizeTitleCandidate(line))
    .filter(Boolean)
    .filter((line) => isProbableTitleLine(line));

  if (introCandidates.length === 0) return "";

  const title = introCandidates[0];
  const firstFront = pairs[0]?.front?.toLowerCase();
  if (firstFront && title.toLowerCase() === firstFront) return "";
  return title;
}

/** Re-export for backward compatibility; implementation lives in detectLanguage (server-safe). */
export { detectLanguageFromText } from "./detectLanguage";

export function detectLanguagePair(pairs: ParsedVocabularyPair[]): {
  sourceLang: DeckLang | null;
  targetLang: DeckLang | null;
} {
  const samplePairs = pairs.slice(0, 40);
  const frontText = samplePairs.map((pair) => pair.front).join("\n");
  const backText = samplePairs.map((pair) => pair.back).join("\n");
  const frontResult = detectLanguageWithScores(frontText);
  const backResult = detectLanguageWithScores(backText);

  let sourceLang = frontResult.code;
  let targetLang = backResult.code;

  if (!sourceLang && targetLang && targetLang !== "en" && containsLatin(frontText)) {
    sourceLang = "en";
  }
  if (!targetLang && sourceLang && sourceLang !== "en" && containsLatin(backText)) {
    targetLang = "en";
  }

  if (sourceLang === targetLang && sourceLang !== null) {
    const frontScore = frontResult.scores[sourceLang] ?? 0;
    const backScore = backResult.scores[targetLang] ?? 0;
    if (frontScore <= 1 && backScore <= 1) {
      sourceLang = null;
      targetLang = null;
    } else if (frontScore < backScore) {
      sourceLang = null;
    } else if (backScore < frontScore) {
      targetLang = null;
    }
  }

  return { sourceLang, targetLang };
}

export function parseFlashcardInput(text: string): ParsedFlashcardInput {
  const normalized = normalizeInput(text);
  const pairs = extractStructuredPairs(normalized);
  const { sourceLang, targetLang } = detectLanguagePair(pairs);
  const title = extractModuleTitle(normalized, pairs);

  return {
    sourceText: normalized,
    sourceSignature: normalized.toLowerCase(),
    title,
    pairs,
    count: pairs.length,
    sourceLang,
    targetLang,
    isStructured: pairs.length > 0,
  };
}

// ─── Multi-topic segmentation ──────────────────────────────────────────────────

/** Regex for lines that look like section headings. */
const HEADING_LINE_REGEX = /^\s*(?:(?:[\p{So}\p{Sk}\p{Sm}]|[\u2600-\u27BF]|[\u{1F300}-\u{1F9FF}])+\s*)?[\p{L}\p{N}\s\-–—:()[\]]+(?:\s*[\(\[].+?[\)\]])?\s*$/u;

/** Separator line: dashes, underscores, equals. */
const SEPARATOR_LINE_REGEX = /^[\s\-–—_=~]{2,}\s*$/;

/** Numbering: "1." "2." "3." or "1)" "2)" at start — strongly suggests section header (not a card). */
const NUMBERED_HEADING_REGEX = /^\s*\d+[.)]\s+/u;

function looksLikeHeading(line: string, _prevLineHadContent: boolean): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (SEPARATOR_LINE_REGEX.test(trimmed)) return false;

  // Numbered lines (1. 2. 3.) are section headers, never cards — check first, before word/length limits
  if (NUMBERED_HEADING_REGEX.test(trimmed)) return true;

  if (trimmed.length > 120) return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length > 12) return false;

  // Lines that parse as pairs are usually cards — unless they're headings (handled above)
  if (parsePairSegment(trimmed)) return false;

  if (HEADING_LINE_REGEX.test(trimmed) && /[\p{L}\p{N}]/u.test(trimmed)) return true;
  return false;
}

function extractTitleAndSubtitle(line: string): { title: string; subtitle?: string } {
  const cleaned = sanitizeTitleCandidate(line);
  // Dash-separated translation: "Topic — Translation" or "Topic – Translation" or "Topic - Translation"
  const dashMatch = cleaned.match(/^(.+?)\s+(?:—|–)\s+(.+)$/u);
  if (dashMatch) {
    const t = dashMatch[1].trim();
    const s = dashMatch[2].trim();
    if (t && s) return { title: t, subtitle: s };
  }
  const parenMatch = cleaned.match(/^(.+?)\s*[\(\[]([^)\]]+)[\)\]]\s*$/);
  if (parenMatch) {
    return { title: parenMatch[1].trim(), subtitle: parenMatch[2].trim() };
  }
  return { title: cleaned };
}

/**
 * Segments input text into topic blocks using heading signals:
 * - Emoji-prefixed lines (e.g. "👦 Parts of the Body")
 * - Title-like lines with optional translation in brackets
 * - Separator lines
 * - Numbering resets (1. 1))
 */
export function segmentTextIntoTopicBlocks(text: string): TopicBlock[] {
  const normalized = normalizeInput(text);
  if (!normalized) return [];

  const lines = normalized.split("\n");
  const blocks: TopicBlock[] = [];
  let currentBlock: { lines: string[]; title: string; subtitle?: string; startLine: number } | null = null;
  let prevLineHadContent = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const isEmpty = !trimmed;

    if (isEmpty) {
      prevLineHadContent = false;
      continue;
    }

    const isSeparator = SEPARATOR_LINE_REGEX.test(trimmed);
    if (isSeparator) {
      if (currentBlock && currentBlock.lines.length > 0) {
        blocks.push({
          rawText: currentBlock.lines.join("\n").trim(),
          title: currentBlock.title,
          subtitle: currentBlock.subtitle,
          startLine: currentBlock.startLine,
        });
      }
      currentBlock = null;
      prevLineHadContent = false;
      continue;
    }

    if (looksLikeHeading(trimmed, prevLineHadContent)) {
      if (currentBlock && currentBlock.lines.length > 0) {
        blocks.push({
          rawText: currentBlock.lines.join("\n").trim(),
          title: currentBlock.title,
          subtitle: currentBlock.subtitle,
          startLine: currentBlock.startLine,
        });
      }
      const { title, subtitle } = extractTitleAndSubtitle(trimmed);
      currentBlock = {
        lines: [],
        title: title || "Untitled",
        subtitle: subtitle || undefined,
        startLine: i,
      };
      prevLineHadContent = true;
      continue;
    }

    if (!currentBlock) {
      currentBlock = {
        lines: [],
        title: "",
        subtitle: undefined,
        startLine: i,
      };
    }
    currentBlock.lines.push(line);
    prevLineHadContent = true;
  }

  if (currentBlock && currentBlock.lines.length > 0) {
    if (!currentBlock.title) {
      const firstLines = currentBlock.lines.slice(0, 5).join(" ");
      const introTitle = extractModuleTitle(currentBlock.lines.join("\n"), extractStructuredPairs(currentBlock.lines.join("\n")));
      currentBlock.title = introTitle || "Vocabulary";
    }
    blocks.push({
      rawText: currentBlock.lines.join("\n").trim(),
      title: currentBlock.title || "Vocabulary",
      subtitle: currentBlock.subtitle,
      startLine: currentBlock.startLine,
    });
  }

  return blocks;
}

/** Min chars for heuristic paragraph splitting; avoids splitting short texts */
const HEURISTIC_SPLIT_MIN_CHARS = 600;
/** Min paragraph clusters to try heuristic split */
const HEURISTIC_SPLIT_MIN_PARAS = 2;

/**
 * Heuristic fallback: when no headings are found, split long text by paragraph clusters.
 * Uses double newlines and treats first line of each cluster as potential section title.
 */
function heuristicSegmentByParagraphs(text: string): TopicBlock[] {
  const normalized = normalizeInput(text);
  if (!normalized || normalized.length < HEURISTIC_SPLIT_MIN_CHARS) return [];

  const paras = normalized.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (paras.length < HEURISTIC_SPLIT_MIN_PARAS) return [];

  const blocks: TopicBlock[] = [];
  paras.forEach((para, i) => {
    const firstLine = para.split("\n")[0]?.trim() ?? "";
    const title =
      firstLine.length <= 60 && firstLine.length >= 2 && !parsePairSegment(firstLine)
        ? firstLine
        : `Section ${i + 1}`;
    blocks.push({
      rawText: para,
      title,
      startLine: i,
    });
  });
  return blocks;
}

/**
 * Segments text for AI multi-topic generation. Uses heading-based segmentation first;
 * if only one block is found but text is long and paragraph-structured, applies heuristic fallback.
 */
export function segmentTextForMultiTopicAI(text: string): TopicBlock[] {
  const byHeadings = segmentTextIntoTopicBlocks(text);
  if (byHeadings.length >= 2) return byHeadings;
  const byHeuristic = heuristicSegmentByParagraphs(text);
  if (byHeuristic.length >= 2) return byHeuristic;
  return byHeadings;
}

/**
 * Parse flashcard input with multi-topic support.
 * If headings/sections are detected, splits into separate modules.
 * Otherwise falls back to single-module (preserves backward compatibility).
 */
export function parseFlashcardInputMulti(text: string): ParsedFlashcardInputMulti {
  const normalized = normalizeInput(text);
  if (!normalized) {
    return { sourceText: "", sourceSignature: "", modules: [], hasMultipleTopics: false, totalCount: 0 };
  }

  const topicBlocks = segmentTextIntoTopicBlocks(normalized);

  if (topicBlocks.length === 0) {
    const single = parseFlashcardInput(normalized);
    return {
      sourceText: single.sourceText,
      sourceSignature: single.sourceSignature,
      modules: single.pairs.length
        ? [
            {
              title: single.title || "Vocabulary",
              pairs: single.pairs,
              sourceLang: single.sourceLang,
              targetLang: single.targetLang,
            },
          ]
        : [],
      hasMultipleTopics: false,
      totalCount: single.count,
    };
  }

  const modules: ParsedModule[] = [];
  for (const block of topicBlocks) {
    const pairs = extractStructuredPairs(block.rawText);
    if (pairs.length === 0) continue;
    const { sourceLang, targetLang } = detectLanguagePair(pairs);
    modules.push({
      title: block.title || "Vocabulary",
      subtitle: block.subtitle,
      pairs,
      sourceLang,
      targetLang,
    });
  }

  return {
    sourceText: normalized,
    sourceSignature: normalized.toLowerCase(),
    modules,
    hasMultipleTopics: modules.length > 1,
    totalCount: modules.reduce((s, m) => s + m.pairs.length, 0),
  };
}

export function pairsToCardDrafts(
  pairs: ParsedVocabularyPair[],
  sourceLang?: string | null,
  targetLang?: string | null
): Card[] {
  return pairs.map((pair) => ({
    front: pair.front,
    back: pair.back,
    frontLang: sourceLang ?? "",
    backLang: targetLang ?? "",
    hint: "",
  }));
}

async function extractTextFromImage(file: File, geminiApiKeys: string[], t: Translator): Promise<string> {
  if (!isSupportedImage(file)) {
    throw new Error(t("deckModal.unsupportedUploadType"));
  }

  const mimeType = IMAGE_MIME_REGEX.test(file.type) ? file.type : "image/png";
  const base64Data = arrayBufferToBase64(await file.arrayBuffer());
  const parts: GeminiContentPart[] = [
    { text: OCR_PROMPT },
    { inlineData: { mimeType, data: base64Data } },
  ];

  const text = await callGemini("", geminiApiKeys, t("deckModal.imageTextExtractionFailed"), {
    maxOutputTokens: 2500,
    temperature: 0.1,
    parts,
  });

  return normalizeInput(text);
}

export async function extractTextFromSupportedFile(
  file: File,
  options: { geminiApiKeys: string[]; t: Translator }
): Promise<string> {
  const { geminiApiKeys, t } = options;
  const name = file.name.toLowerCase();

  if (name.endsWith(".txt")) {
    return normalizeInput(await file.text());
  }

  if (name.endsWith(".pdf")) {
    const pdfjsLib = await import("pdfjs-dist");
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url
    ).toString();

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages: string[] = [];
    for (let i = 1; i <= Math.min(pdf.numPages, 20); i += 1) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      pages.push(content.items.map((item: { str?: string }) => item.str ?? "").join(" "));
    }
    return normalizeInput(pages.join("\n"));
  }

  if (name.endsWith(".doc") || name.endsWith(".docx")) {
    const mammoth = await import("mammoth");
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return normalizeInput(result.value);
  }

  if (isSupportedImage(file)) {
    return extractTextFromImage(file, geminiApiKeys, t);
  }

  throw new Error(t("deckModal.unsupportedUploadType"));
}
